import { v, type Infer } from "convex/values";
import { isMemberOf } from "../validation/narrow";

export const RECORD_FIELD_KINDS = [
  "text",
  "enum",
  "number",
  "currency",
  "date",
  "boolean",
  "reference",
  "user",
  "location",
] as const;

export type RecordFieldKind = (typeof RECORD_FIELD_KINDS)[number];

export const filterOperatorValidator = v.union(
  v.literal("is"),
  v.literal("is_not"),
  v.literal("is_any_of"),
  v.literal("is_none_of"),
  v.literal("contains"),
  v.literal("not_contains"),
  v.literal("starts_with"),
  v.literal("is_known"),
  v.literal("is_unknown"),
  v.literal("gt"),
  v.literal("gte"),
  v.literal("lt"),
  v.literal("lte"),
  v.literal("between"),
  v.literal("before"),
  v.literal("after"),
  v.literal("on"),
  v.literal("in_last"),
  v.literal("more_than_ago"),
  v.literal("within"),
);

export type FilterOperator = Infer<typeof filterOperatorValidator>;

export const filterValueValidator = v.union(
  v.string(),
  v.number(),
  v.boolean(),
  v.null(),
  v.array(v.string()),
  v.array(v.number()),
);

export type FilterValue = Infer<typeof filterValueValidator>;

export const filterClauseValidator = v.object({
  field: v.string(),
  operator: filterOperatorValidator,
  value: v.optional(filterValueValidator),
});

export type RecordCondition = Infer<typeof filterClauseValidator>;

export const recordSortValidator = v.object({
  field: v.string(),
  direction: v.union(v.literal("asc"), v.literal("desc")),
});

export const recordTotalValidator = v.union(
  v.object({ kind: v.literal("exact"), value: v.number() }),
  v.object({ kind: v.literal("atLeast"), value: v.number() }),
  v.object({ kind: v.literal("unknown") }),
);

export interface RecordFilterGroup {
  conjunction: "and";
  conditions: RecordCondition[];
}

export const OPERATORS_BY_KIND: Record<RecordFieldKind, FilterOperator[]> = {
  text: [
    "contains",
    "not_contains",
    "starts_with",
    "is",
    "is_not",
    "is_known",
    "is_unknown",
  ],
  enum: ["is", "is_not", "is_any_of", "is_none_of", "is_known", "is_unknown"],
  number: [
    "is",
    "is_not",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "is_known",
    "is_unknown",
  ],
  currency: [
    "is",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "is_known",
    "is_unknown",
  ],
  date: [
    "on",
    "before",
    "after",
    "between",
    "in_last",
    "more_than_ago",
    "is_known",
    "is_unknown",
  ],
  boolean: ["is"],
  reference: [
    "is",
    "is_not",
    "is_any_of",
    "is_none_of",
    "is_known",
    "is_unknown",
  ],
  user: ["is", "is_not", "is_any_of", "is_none_of", "is_known", "is_unknown"],
  location: ["within"],
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: "is",
  is_not: "is not",
  is_any_of: "is any of",
  is_none_of: "is none of",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  is_known: "is known",
  is_unknown: "is unknown",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
  before: "is before",
  after: "is after",
  on: "is on",
  in_last: "is in the last",
  more_than_ago: "is more than",
  within: "is within",
};

export const SINGLE_CHOICE_OPERATORS: FilterOperator[] = ["is"];

const NO_VALUE_OPERATORS: FilterOperator[] = ["is_known", "is_unknown"];
const TWO_VALUE_OPERATORS: FilterOperator[] = ["between", "within"];
const MULTI_VALUE_OPERATORS: FilterOperator[] = ["is_any_of", "is_none_of"];

export type OperatorArity = "none" | "one" | "two" | "many";

export function operatorArity(operator: FilterOperator): OperatorArity {
  if (NO_VALUE_OPERATORS.includes(operator)) return "none";
  if (TWO_VALUE_OPERATORS.includes(operator)) return "two";
  if (MULTI_VALUE_OPERATORS.includes(operator)) return "many";
  return "one";
}

function isPresent(value: string | number | boolean): boolean {
  return typeof value === "string" ? value.trim() !== "" : true;
}

const ZIP_PATTERN = /^\d{5}$/;

export function zipRadiusOf(
  value: FilterValue | undefined,
): { zip: string; radiusMiles: number } | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const [zip = "", miles] = value.map(String);
  const radiusMiles = Number(miles);
  return ZIP_PATTERN.test(zip) && radiusMiles > 0 ? { zip, radiusMiles } : null;
}

export function isConditionComplete(condition: RecordCondition): boolean {
  const arity = operatorArity(condition.operator);
  if (arity === "none") return true;
  if (condition.operator === "within") {
    return zipRadiusOf(condition.value) !== null;
  }
  if (condition.value === undefined || condition.value === null) return false;
  if (Array.isArray(condition.value)) {
    if (arity === "two") {
      return condition.value.length === 2 && condition.value.every(isPresent);
    }
    return condition.value.length > 0 && condition.value.every(isPresent);
  }
  return isPresent(condition.value);
}

export type OptionSetOperator = "is" | "is_not";

const MANY_FORM: Record<OptionSetOperator, FilterOperator> = {
  is: "is_any_of",
  is_not: "is_none_of",
};

export function optionSetOperator(
  operator: FilterOperator,
): OptionSetOperator | undefined {
  switch (operator) {
    case "is":
    case "is_any_of":
      return "is";
    case "is_not":
    case "is_none_of":
      return "is_not";
    case "after":
    case "before":
    case "between":
    case "contains":
    case "gt":
    case "gte":
    case "in_last":
    case "is_known":
    case "is_unknown":
    case "lt":
    case "lte":
    case "more_than_ago":
    case "not_contains":
    case "on":
    case "starts_with":
    case "within":
      return undefined;
    default: {
      const unhandled: never = operator;
      void unhandled;
      return undefined;
    }
  }
}

export function supportsOptionSet(
  operators: readonly FilterOperator[],
  operator: OptionSetOperator,
): boolean {
  return (
    operators.includes(operator) && operators.includes(MANY_FORM[operator])
  );
}

export function optionSetCondition(
  field: string,
  operator: OptionSetOperator,
  values: string[],
): RecordCondition {
  return values.length === 1
    ? { field, operator, value: values[0] }
    : { field, operator: MANY_FORM[operator], value: values };
}

export function emptyFilterGroup(): RecordFilterGroup {
  return { conjunction: "and", conditions: [] };
}

export const textMatchOperatorValidator = v.union(
  v.literal("contains"),
  v.literal("not_contains"),
  v.literal("starts_with"),
  v.literal("is"),
  v.literal("is_not"),
);

export type TextMatchOperator = Infer<typeof textMatchOperatorValidator>;

export function toTextMatchOperator(
  operator: FilterOperator,
): TextMatchOperator | undefined {
  switch (operator) {
    case "contains":
    case "not_contains":
    case "starts_with":
    case "is":
    case "is_not":
      return operator;
    case "after":
    case "before":
    case "between":
    case "gt":
    case "gte":
    case "in_last":
    case "is_any_of":
    case "is_known":
    case "is_none_of":
    case "is_unknown":
    case "lt":
    case "lte":
    case "more_than_ago":
    case "on":
    case "within":
      return undefined;
    default: {
      const unhandled: never = operator;
      void unhandled;
      return undefined;
    }
  }
}

export function matchesText(
  haystack: string | undefined,
  needle: string,
  operator: TextMatchOperator,
): boolean {
  const subject = (haystack ?? "").trim().toLowerCase();
  const term = needle.trim().toLowerCase();
  if (term === "") return true;
  switch (operator) {
    case "contains":
      return subject.includes(term);
    case "not_contains":
      return !subject.includes(term);
    case "starts_with":
      return subject.startsWith(term);
    case "is":
      return subject === term;
    case "is_not":
      return subject !== term;
  }
}

export function enumMembers<TMember extends string>(
  condition: RecordCondition | undefined,
  allowed: readonly TMember[],
): TMember[] | undefined {
  if (!condition) return undefined;
  const raw =
    condition.value === undefined || condition.value === null
      ? []
      : Array.isArray(condition.value)
        ? condition.value
        : [condition.value];
  const selected = raw
    .map(String)
    .filter((value): value is TMember => isMemberOf(allowed, value));
  switch (condition.operator) {
    case "is":
    case "is_any_of":
      return selected.length > 0 ? selected : undefined;
    case "is_not":
    case "is_none_of":
      return selected.length > 0
        ? allowed.filter((value) => !selected.includes(value))
        : undefined;
    case "is_known":
      return [...allowed];
    case "is_unknown":
      return [];
    case "after":
    case "before":
    case "between":
    case "contains":
    case "gt":
    case "gte":
    case "in_last":
    case "lt":
    case "lte":
    case "more_than_ago":
    case "not_contains":
    case "on":
    case "starts_with":
    case "within":
      return undefined;
    default: {
      const unhandled: never = condition.operator;
      void unhandled;
      return undefined;
    }
  }
}
